import React from "react";
import { FORM_VARIANTS } from "../../utility/Enum";
import {
  FieldContent,
  FieldDescription,
  FieldSet,
  FieldTitle,
} from "../../shadcn/components/ui/field";

const groupByRow = (schema) => {
  const group = {};
  schema.map((field) => {
    if (!field || !field.row) {
      return;
    }

    if (group[field.row]) {
      group[field.row] = [...group[field.row], field];
    } else {
      group[field.row] = [field];
    }
  });

  return group;
};

const groupBySection = (schema) => {
  const group = {};
  schema.map((field) => {
    if (!field || !field.section || !field.section.title) {
      return;
    }

    if (group[field.section.title]) {
      group[field.section.title] = [...group[field.section.title], field];
    } else {
      group[field.section.title] = [field];
    }
  });

  return group;
};
// Static class names so Tailwind can see them (grid has 12 columns on md+).
const MD_SPAN = {
  1: "md:col-span-1",
  2: "md:col-span-2",
  3: "md:col-span-3",
  4: "md:col-span-4",
  5: "md:col-span-5",
  6: "md:col-span-6",
  7: "md:col-span-7",
  8: "md:col-span-8",
  9: "md:col-span-9",
  10: "md:col-span-10",
  11: "md:col-span-11",
  12: "md:col-span-12",
};

const mdSpan = (value) => MD_SPAN[Math.round(value)] || MD_SPAN[12];

const renderColumnComponents =
  ({ columnLength, onChange, values, errors, maxLength = 12 }) =>
  (column) => {
    const renderer = column.renderer
      ? column.renderer({
          ...column,
          value: values[column.name],
          onChange,
          error: errors[column.name],
          values,
        })
      : null;

    // Section rows use an 8-column area; scale spans to the 12-column grid.
    const span = (column.md || maxLength / columnLength) * (12 / maxLength);
    return (
      <div
        key={column.name}
        className={`col-span-12 min-w-0 ${mdSpan(span)} ${
          column.hidden ? "hidden" : ""
        }`}
      >
        {React.isValidElement(renderer) ? renderer : null}
      </div>
    );
  };

const Form = ({
  onChange,
  values,
  schema = [],
  errors = {},
  variant = FORM_VARIANTS.fields,
}) => {
  let components;
  if (variant === FORM_VARIANTS.sections) {
    const groupedBySection = groupBySection(schema);

    components = Object.entries(groupedBySection).map(([section, fields]) => {
      const groupedByRow = groupByRow(fields);
      const sectionDetails = fields[0].section;
      return (
        // Section layout from the @shadcndesign settings blocks: title and
        // description on the left, fields on the right.
        <FieldSet
          key={section}
          aria-labelledby={`form-section-${section}`}
          className="col-span-12 border-t border-border pt-6 first:border-t-0 first:pt-0"
        >
          <div className="grid grid-cols-1 gap-4 md:grid-cols-3 md:gap-6">
            <FieldContent>
              <FieldTitle id={`form-section-${section}`}>
                {sectionDetails.title}
              </FieldTitle>
              {sectionDetails.subtitle ? (
                <FieldDescription>{sectionDetails.subtitle}</FieldDescription>
              ) : null}
            </FieldContent>
            <div className="grid grid-cols-12 gap-2 md:col-span-2">
              {Object.entries(groupedByRow).map(([row, columns]) =>
                columns.map(
                  renderColumnComponents({
                    columnLength: columns.length,
                    onChange,
                    values,
                    errors,
                    maxLength: 8,
                  })
                )
              )}
            </div>
          </div>
        </FieldSet>
      );
    });
  } else {
    const group = groupByRow(schema);
    components = Object.entries(group).map(([row, columns]) => {
      return columns.map(
        renderColumnComponents({
          columnLength: columns.length,
          onChange,
          values,
          errors,
        })
      );
    });
  }

  return (
    // Fields share a row only from md up. Below that, 11 column gaps would
    // make the grid wider than a phone-width drawer.
    <div className="grid grid-cols-12 gap-y-6 md:gap-x-6">
      {components}
    </div>
  );
};

export default Form;
